import { EventBus } from "../events/EventBus";
import { FlowDefinition } from "./FlowDefinition";
import { FlowRunner } from "./FlowRunner";
import { FlowPersistence } from "./FlowPersistence";

// =============================================================================
// WorkFlowOrchestrator: Manages multiple flows, centralizes execution control,
// and logs step execution events. It can start, pause, resume, and cancel flows.
// =============================================================================

export class WorkFlowOrchestrator<Context> {
  private flows: Map<string, FlowDefinition> = new Map(); // Registered flows
  private activeRunners: Map<string, FlowRunner> = new Map(); // Active flow runners
  private logs: Map<string, any[]> = new Map(); // Logs indexed by flowId
  public verbose: boolean = false; // Controls logging output
  private eventBus: EventBus; // Event bus for handling events

  constructor(eventBus: EventBus, verbose: boolean = false) {
    this.eventBus = eventBus;
    this.verbose = verbose;

    // Listen for step execution logs and store them
    this.eventBus.on({
      eventName: "log:stepExecution",
      handler: (payload) => {
        const data = payload.data as any;
        if (data && data.flowId) {
          if (!this.logs.has(data.flowId)) {
            this.logs.set(data.flowId, []);
          }
          this.logs.get(data.flowId)!.push(data);
        }
      }
    });
  }

  // Registers a new flow definition
  addFlow(flow: FlowDefinition): void {
    this.flows.set(flow.name, flow);
  }

  // Starts execution of a flow with a given name and context
  execute(flowName: string, enablePersistence: boolean = false): string {
    if (!this.flows.has(flowName)) {
      throw new Error(`Flow ${flowName} not found`);
    }

    const selectedFlow = this.flows.get(flowName)!;
    const runner = new FlowRunner(selectedFlow, this.eventBus, enablePersistence);
    runner.verbose = this.verbose;
    this.activeRunners.set(runner.id, runner);

    if (this.verbose) this.localLog(runner.id, `Flow ${flowName} started.`);

    runner.execute();
    return runner.id;
  }

  // Pauses execution of a running flow by ID
  pause(flowId: string): void {
    const runner = this.activeRunners.get(flowId);
    if (runner) {
      runner.pause();
      if (this.verbose) this.localLog(flowId, `Flow paused.`);
    }
  }

  // Resumes execution of a paused flow by ID
  resume(flowId: string): void {
    const runner = this.activeRunners.get(flowId);
    if (runner) {
      runner.resume();
      if (this.verbose) this.localLog(flowId, `Flow resumed.`);
    }
  }

  // Cancels execution of a running flow by ID
  cancel(flowId: string): void {
    const runner = this.activeRunners.get(flowId);
    if (runner) {
      runner.cancel();
      if (this.verbose) this.localLog(flowId, `Flow cancelled.`);
    }
  }

  // Retrieves execution logs for a given flow ID
  getFlowLogs(flowId: string): any {
    return this.logs.get(flowId) || [];
  }

  // Resume a flow from persisted state
  async resumeFlow(flowName: string): Promise<string | null> {
    if (!this.flows.has(flowName)) {
      throw new Error(`Flow ${flowName} not found`);
    }

    const persistedState = FlowPersistence.loadFlowState(flowName);
    if (!persistedState) {
      if (this.verbose) this.localLog('unknown', `No persisted state found for flow ${flowName}`);
      return null;
    }

    try {
      const selectedFlow = this.flows.get(flowName)!;
      const runner = await FlowRunner.restoreFromState(selectedFlow, this.eventBus, persistedState);
      runner.verbose = this.verbose;
      this.activeRunners.set(runner.id, runner);

      if (this.verbose) this.localLog(runner.id, `Flow ${flowName} resumed from persisted state.`);

      await runner.resumeFromState();
      return runner.id;
    } catch (error) {
      console.error(`[FlowOrchestrator] Failed to resume flow ${flowName}:`, error);
      FlowPersistence.clearFlowState(flowName);
      return null;
    }
  }

  // Check if a flow has persisted state
  hasPersistedFlow(flowName: string): boolean {
    return FlowPersistence.hasPersistedState(flowName);
  }

  // Clear persisted state for a flow
  clearPersistedFlow(flowName: string): void {
    FlowPersistence.clearFlowState(flowName);
  }

  // Cleanup old persisted flows
  cleanupPersistedFlows(): void {
    FlowPersistence.cleanup();
  }

  // Internal method to log messages and store them in the logs map
  private localLog(flowId: string, message: string): void {
    console.log(`[FlowOrchestrator ${flowId}] ${message}`);

    if (!this.logs.has(flowId)) {
      this.logs.set(flowId, []);
    }

    this.logs.get(flowId)!.push({ timestamp: new Date().toISOString(), message });
  }
}